import { useEffect, useRef } from "react";
import Cookies from "js-cookie";
import { extendSessionToken, getActiveSessionToken, getTokenExpiryMs, verifyToken } from "./authHelpers";
import {
    SESSION_CHANNEL_NAME,
    SESSION_EXPIRY_GRACE_MS,
    SESSION_HEARTBEAT_MS,
    SESSION_HEARTBEAT_STALE_MS,
    SESSION_ACTIVE_WINDOW_MS,
    SESSION_AUTO_EXTEND_THRESHOLD_MS,
    SESSION_MODAL_THRESHOLD_MS,
    SESSION_REFRESH_JITTER_MAX_MS,
    SessionSyncMessage,
    getOrCreateSessionTabId,
    readLeaderTabState,
    writeLeaderTabState,
    clearLeaderTabState,
    isSessionRetryInProgress,
    startSessionRetry,
    isSessionRefreshInProgress,
    beginSessionRefresh,
    endSessionRefresh,
} from "./sessionManager";
import { ActionTypes } from "../context";

export interface SessionManagerParams {
    dispatch: React.Dispatch<{ type: ActionTypes; payload?: any }>;
    setToken: (token: string) => void;
    removeToken: () => void;
    accessToken: string | undefined;
    sessionExpired: boolean;
}

// Warns users when their session is about to expire.
// Allows them to manually extend or expire the session via a modal.
// Automatically extends the session when the user is active and the token is close to expiry.
// Ensures only one browser tab shows the modal (the leader tab).
// Ensures all other tabs (followers) listen and react without showing their own modal.
// Uses a heartbeat mechanism so leadership is reliable even if a tab crashes or closes.
// Uses jittered token refreshes to avoid multiple tabs refreshing at the same moment.
// Ensures jitter never pushes refresh past the token's expiry time.
// Once the token has expired (plus a short grace) every tab logs itself out;
// the leader broadcasts SESSION_EXPIRE first so followers go together.
export function useSessionManager({
    dispatch,
    setToken,
    removeToken,
    accessToken,
    sessionExpired,
}: SessionManagerParams): void {
    const tabIdRef = useRef<string>(getOrCreateSessionTabId());
    const sessionChannelRef = useRef<BroadcastChannel | null>(null);
    const isLeaderTabRef = useRef<boolean>(false);
    const leaderTabIdRef = useRef<string | null>(null);
    const lastLeaderTabHeartbeatRef = useRef<number>(0);
    const lastActivityRef = useRef<number>(Date.now());
    const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastAutoRefreshAttemptRef = useRef<number>(0);
    const sessionExpiredRef = useRef<boolean>(sessionExpired);
    const accessTokenRef = useRef<string | undefined>(accessToken);
    const hardExpiryHandledRef = useRef<boolean>(false);

    const isTabRecentlyActive = (): boolean => {
        return Date.now() - lastActivityRef.current <= SESSION_ACTIVE_WINDOW_MS;
    };

    const broadcastSessionMessage = (message: SessionSyncMessage): void => {
        const channel = sessionChannelRef.current;
        if (!channel) {
            return;
        }
        try {
            channel.postMessage(message);
        } catch (error) {
            console.warn("Unable to broadcast session message", error);
        }
    };

    // Active leader tab persisted in localStorage so follower tabs can discover it.
    const becomeLeaderTab = (reason: string): void => {
        const now = Date.now();
        isLeaderTabRef.current = true;
        leaderTabIdRef.current = tabIdRef.current;
        lastLeaderTabHeartbeatRef.current = now;

        writeLeaderTabState({
            tabId: tabIdRef.current,
            timestamp: now,
            lastActivity: lastActivityRef.current,
        });

        broadcastSessionMessage({
            type: "LEADER_TAB_CHANGE",
            tabId: tabIdRef.current,
            leaderTabId: tabIdRef.current,
            timestamp: now,
        });

        console.log("[Session Leader Tab] Leader acquired", { tabId: tabIdRef.current, reason });
    };

    const resignLeaderTab = (reason: string): void => {
        if (!isLeaderTabRef.current) {
            return;
        }

        isLeaderTabRef.current = false;
        leaderTabIdRef.current = null;
        clearLeaderTabState(tabIdRef.current);

        broadcastSessionMessage({
            type: "LEADER_TAB_CHANGE",
            tabId: tabIdRef.current,
            leaderTabId: null,
            timestamp: Date.now(),
        });

        console.log("[Session Leader Tab] Leader released", { tabId: tabIdRef.current, reason });
    };

    const triggerHardSessionExpiry = (reason: string): void => {
        if (hardExpiryHandledRef.current) {
            return;
        }

        hardExpiryHandledRef.current = true;
        console.warn("[Session] Hard expiry triggered", { reason });

        // Followers only hard-expire on this message, so the leader sends it
        // before tearing its own session down.
        if (isLeaderTabRef.current) {
            broadcastSessionMessage({
                type: "SESSION_EXPIRE",
                tabId: tabIdRef.current,
                timestamp: Date.now(),
            });
        }

        removeToken();
        Cookies.set("logged_out", "true", { path: "/" });
        globalThis.location.href = "/logout";
    };

    // Runs every tick once the token is past its expiry. A refresh that is
    // still in flight (auto or via the modal) gets to finish first; after the
    // grace window the tab logs out regardless of leadership, because both the
    // in-memory token and the shared cookie are dead.
    const handleExpiredToken = (remainingMs: number): void => {
        if (isSessionRefreshInProgress()) {
            return;
        }

        if (remainingMs > -SESSION_EXPIRY_GRACE_MS) {
            // Inside the grace window: surface the modal state so the UI can
            // tell the user, but give a last-second refresh a chance to land.
            if (isLeaderTabRef.current && !sessionExpiredRef.current) {
                dispatch({ type: ActionTypes.SESSION_EXPIRED });
            }
            return;
        }

        triggerHardSessionExpiry("token-expired");
    };

    const attemptLeaderTabAutoRefresh = async (reason: string): Promise<void> => {
        if (!isLeaderTabRef.current || sessionExpiredRef.current || isSessionRefreshInProgress()) {
            return;
        }

        const now = Date.now();
        if (now - lastAutoRefreshAttemptRef.current < 15_000) {
            return;
        }
        lastAutoRefreshAttemptRef.current = now;

        const candidateToken = getActiveSessionToken(accessTokenRef.current);
        const expiryMs = getTokenExpiryMs(candidateToken);
        if (!candidateToken || !expiryMs) {
            return;
        }

        const remainingMs = expiryMs - now;
        if (remainingMs <= 0) {
            // Too late to refresh: the expiry tick owns this case.
            return;
        }

        const maxSafeJitter = Math.max(0, Math.min(SESSION_REFRESH_JITTER_MAX_MS, remainingMs - 1000));
        const jitterMs = Math.floor(Math.random() * (maxSafeJitter + 1));

        if (refreshTimeoutRef.current) {
            clearTimeout(refreshTimeoutRef.current);
        }

        refreshTimeoutRef.current = setTimeout(async () => {
            if (!isLeaderTabRef.current || sessionExpiredRef.current) {
                return;
            }

            if (!beginSessionRefresh()) {
                return;
            }
            try {
                const nextToken = await extendSessionToken(accessTokenRef.current);
                setToken(nextToken);
                startSessionRetry();

                if (sessionExpiredRef.current) {
                    dispatch({ type: ActionTypes.SESSION_RESTORED });
                }

                broadcastSessionMessage({
                    type: "SESSION_EXTEND",
                    tabId: tabIdRef.current,
                    timestamp: Date.now(),
                });
                globalThis.dispatchEvent(new Event("mercury:session-restored"));
                console.log("[Session] Auto refresh succeeded", { reason, jitterMs });
            } catch (error) {
                console.warn("[Session] Auto refresh failed", { reason, error });
            } finally {
                endSessionRefresh();
            }
        }, jitterMs);
    };

    useEffect(() => {
        sessionExpiredRef.current = sessionExpired;
        accessTokenRef.current = accessToken;
        if (!sessionExpired) {
            hardExpiryHandledRef.current = false;
        }
    }, [accessToken, sessionExpired]);

    useEffect(() => {
        const token = Cookies.get("mercury_shared_token");
        if (token && verifyToken(token) && !accessTokenRef.current) {
            dispatch({ type: ActionTypes.UPDATE_TOKEN, payload: token });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Ensures all other tabs (followers) listen and react without showing their own modal.
    useEffect(() => {
        if (globalThis.BroadcastChannel === undefined) {
            console.warn("BroadcastChannel is not available. Session tab leader is disabled.");
            return;
        }

        const channel = new globalThis.BroadcastChannel(SESSION_CHANNEL_NAME);
        sessionChannelRef.current = channel;

        const onMessage = (event: MessageEvent<SessionSyncMessage>) => {
            const payload = event.data;
            if (!payload || payload.tabId === tabIdRef.current) {
                return;
            }

            if (payload.type === "HEARTBEAT") {
                leaderTabIdRef.current = payload.tabId;
                lastLeaderTabHeartbeatRef.current = payload.timestamp;
                return;
            }

            if (payload.type === "LEADER_TAB_CHANGE") {
                leaderTabIdRef.current = payload.leaderTabId || null;
                lastLeaderTabHeartbeatRef.current = payload.timestamp;
                return;
            }

            if (payload.type === "SESSION_EXTEND") {
                const freshCookieToken = Cookies.get("mercury_shared_token");
                if (freshCookieToken && verifyToken(freshCookieToken)) {
                    setToken(freshCookieToken);
                }
                if (sessionExpiredRef.current) {
                    dispatch({ type: ActionTypes.SESSION_RESTORED });
                }
                globalThis.dispatchEvent(new Event("mercury:session-restored"));
                return;
            }

            if (payload.type === "SESSION_EXPIRE") {
                triggerHardSessionExpiry("leader-tab-broadcast-expire");
            }
        };

        channel.onmessage = onMessage;

        const candidateLeaderTab = readLeaderTabState();
        const now = Date.now();
        if (!candidateLeaderTab || now - candidateLeaderTab.timestamp > SESSION_HEARTBEAT_STALE_MS) {
            becomeLeaderTab("startup");
        } else {
            leaderTabIdRef.current = candidateLeaderTab.tabId;
            lastLeaderTabHeartbeatRef.current = candidateLeaderTab.timestamp;
            isLeaderTabRef.current = candidateLeaderTab.tabId === tabIdRef.current;
        }

        return () => {
            channel.close();
            sessionChannelRef.current = null;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Activity listeners continuously refresh each tab's last-active timestamp.
    useEffect(() => {
        const markActive = () => {
            lastActivityRef.current = Date.now();
            if (isLeaderTabRef.current) {
                writeLeaderTabState({
                    tabId: tabIdRef.current,
                    timestamp: Date.now(),
                    lastActivity: lastActivityRef.current,
                });
            }
        };

        const releaseIfLeaderTab = () => {
            resignLeaderTab("tab-unload");
        };

        const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];
        events.forEach(e => globalThis.addEventListener(e, markActive, { passive: true }));
        globalThis.addEventListener("pagehide", releaseIfLeaderTab);
        globalThis.addEventListener("beforeunload", releaseIfLeaderTab);

        return () => {
            events.forEach(e => globalThis.removeEventListener(e, markActive));
            globalThis.removeEventListener("pagehide", releaseIfLeaderTab);
            globalThis.removeEventListener("beforeunload", releaseIfLeaderTab);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Uses a heartbeat mechanism so leadership is reliable even if a tab crashes or closes.
    useEffect(() => {
        const heartbeatId = setInterval(() => {
            if (!isLeaderTabRef.current) {
                return;
            }

            const now = Date.now();
            writeLeaderTabState({
                tabId: tabIdRef.current,
                timestamp: now,
                lastActivity: lastActivityRef.current,
            });

            broadcastSessionMessage({
                type: "HEARTBEAT",
                tabId: tabIdRef.current,
                timestamp: now,
            });
        }, SESSION_HEARTBEAT_MS);

        return () => clearInterval(heartbeatId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Followers promote themselves when leader heartbeats go stale and this tab is still active.
    useEffect(() => {
        const promotionId = setInterval(() => {
            if (isLeaderTabRef.current || !isTabRecentlyActive()) {
                return;
            }

            const now = Date.now();
            const persistedLeaderTab = readLeaderTabState();
            const leaderTimedOut = !persistedLeaderTab || (now - persistedLeaderTab.timestamp > SESSION_HEARTBEAT_STALE_MS);
            const heartbeatTimedOut = !leaderTabIdRef.current || (now - lastLeaderTabHeartbeatRef.current > SESSION_HEARTBEAT_STALE_MS);
            const leaderInactive = Boolean(
                persistedLeaderTab && now - persistedLeaderTab.lastActivity > SESSION_ACTIVE_WINDOW_MS
            );

            if (leaderTimedOut || heartbeatTimedOut) {
                becomeLeaderTab(leaderTimedOut ? "persisted-heartbeat-timeout" : "channel-heartbeat-timeout");
                return;
            }

            if (leaderInactive && persistedLeaderTab?.tabId !== tabIdRef.current) {
                becomeLeaderTab("leader-inactive-takeover");
            }
        }, 1000);

        return () => clearInterval(promotionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Only the leader tab auto-extends or shows the modal. Every tab watches
    // for hard expiry, reading the shared cookie so a follower with a stale
    // in-memory token is not logged out after a successful leader refresh.
    useEffect(() => {
        const tick = () => {
            const activeToken = getActiveSessionToken(accessTokenRef.current);
            const expiryMs = getTokenExpiryMs(activeToken);

            if (!activeToken || !expiryMs) {
                return;
            }

            const remainingMs = expiryMs - Date.now();
            if (remainingMs <= 0) {
                handleExpiredToken(remainingMs);
                return;
            }

            if (!isLeaderTabRef.current) {
                // Ensures only one browser tab shows the modal (the leader tab).
                return;
            }

            // Auto-extend is suppressed whenever the session-expired modal is already open.
            if (sessionExpiredRef.current) {
                return;
            }

            if (isTabRecentlyActive()) {
                if (remainingMs <= SESSION_AUTO_EXTEND_THRESHOLD_MS) {
                    void attemptLeaderTabAutoRefresh("active-window");
                }
                return;
            }

            if (remainingMs <= SESSION_MODAL_THRESHOLD_MS && !isSessionRetryInProgress()) {
                // Warns users when their session is about to expire.
                dispatch({ type: ActionTypes.SESSION_EXPIRED });
            }
        };

        tick();
        const intervalId = setInterval(tick, 1000);
        return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    // Cleanup refresh timeout on unmount.
    useEffect(() => {
        return () => {
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
                refreshTimeoutRef.current = null;
            }
        };
    }, []);
}
